import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Avatar,
  Badge
} from '@mui/material';
import {
  Dashboard,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Schedule,
  Person,
  Business,
  Refresh,
  Assignment,
  AssignmentTurnedIn,
  AssignmentLate,
  AssignmentInd,
  Notifications,
  Security,
  Speed,
  Assessment
} from '@mui/icons-material';
import { getDashboard, getAlerts } from '../Services/monitoringService';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function MonitoringPage() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const { showToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  const calculateAge = (dateCreation) => {
    if (!dateCreation) return 0;
    
    const creationDate = new Date(dateCreation);
    
    // Vérifier si la date est valide
    if (isNaN(creationDate.getTime())) {
      console.warn('Date invalide:', dateCreation);
      return 0;
    }
    
    const now = new Date();
    const diffTime = Math.abs(now - creationDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return isNaN(diffDays) ? 0 : diffDays;
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [dashboardData, alertsData] = await Promise.all([
        getDashboard(),
        getAlerts()
      ]);
      setDashboard(dashboardData);
      setAlerts(alertsData);
    } catch (error) {
      showToast('Erreur lors du chargement du monitoring', { severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'error': return <Warning color="error" />;
      case 'warning': return <Warning color="warning" />;
      case 'info': return <Schedule color="info" />;
      default: return <CheckCircle color="success" />;
    }
  };

  const getAlertSeverity = (type) => {
    switch (type) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'success';
    }
  };

  const handleMetricClick = (filterType) => {
    // Rediriger vers la page des tickets avec le filtre correspondant
    navigate('/tickets', { 
      state: { 
        filter: filterType,
        openCreate: false 
      } 
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboard) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Impossible de charger les données de monitoring
        </Alert>
      </Box>
    );
  }

  const { metrics, ticketsByStatus, ticketsByCollaborateur, ticketsByClient, evolution, overdueTickets, activeTickets, alerts: dashboardAlerts } = dashboard;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <Box sx={{ 
        flexGrow: 1,
        background: '#f5f5f5',
        p: 3
      }}>
      <Box sx={{ 
        background: 'white',
        borderRadius: 2,
        p: 3,
        mb: 3,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e0e0e0'
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ 
              bgcolor: '#ea5b0c',
              width: 48,
              height: 48
            }}>
              <Dashboard />
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" sx={{ 
                fontWeight: 600,
                color: '#313635',
                mb: 0.5
              }}>
                Monitoring
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Surveillance en temps réel des tickets et alertes
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            
              <IconButton onClick={loadDashboard} color="primary" sx={{
                bgcolor: '#ea5b0c',
                color: 'white',
                '&:hover': {
                  bgcolor: '#d14a0a'
                }
              }}>
                <Refresh />
              </IconButton>
            
          </Stack>
        </Stack>
      </Box>

      {(alerts.length > 0 || overdueTickets.length > 0) && (
        <Box sx={{ mb: 3 }}>
          <Card sx={{
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Badge badgeContent={alerts.length + overdueTickets.length} color="error">
                  <Notifications color="primary" />
                </Badge>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#313635' }}>
                  Alertes Actives
                </Typography>
              </Stack>
              <Stack spacing={2}>
                {/* Alertes existantes */}
                {alerts.map((alert, index) => (
                  <Alert
                    key={index}
                    severity={getAlertSeverity(alert.type)}
                    icon={getAlertIcon(alert.type)}
                    sx={{
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        width: '100%'
                      }
                    }}
                    action={
                      <Chip 
                        label={alert.count} 
                        size="small" 
                        color={getAlertSeverity(alert.type)}
                        sx={{ fontWeight: 600 }}
                      />
                    }
                  >
                    <AlertTitle sx={{ fontWeight: 600 }}>{alert.title}</AlertTitle>
                    {alert.message}
                  </Alert>
                ))}
                
                {/* Tickets en retard */}
                {overdueTickets.length > 0 && (
                  <Alert
                    severity="error"
                    icon={<Warning />}
                    sx={{
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        width: '100%'
                      }
                    }}
                    action={
                      <Chip 
                        label={overdueTickets.length} 
                        size="small" 
                        color="error"
                        sx={{ fontWeight: 600 }}
                      />
                    }
                  >
                    <AlertTitle sx={{ fontWeight: 600 }}>Tickets en Retard</AlertTitle>
                    {overdueTickets.length} ticket(s) non résolu(s) depuis plus de 7 jours
                    <Box sx={{ mt: 2 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Ticket</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Titre</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Retard (jours)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {overdueTickets.slice(0, 3).map((ticket, index) => (
                              <TableRow key={index} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}>
                                <TableCell>
                                  <Chip 
                                    label={ticket.numeroTicket || 'N/A'} 
                                    size="small" 
                                    color="error"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                    {ticket.titre || 'Sans titre'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Chip 
                                    label={`${calculateAge(ticket.dateCreation)}j`} 
                                    size="small" 
                                    color="error"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}

{/* Métriques principales */}
      <Grid container spacing={3} sx={{ mb: 3 }} justifyContent="center">
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', minWidth: 0 }}>
          <Card 
            onClick={() => handleMetricClick('all')}
            sx={{
              background: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                borderColor: '#ea5b0c'
              }
            }}
          >
            <CardContent sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              width: '100%',
              minWidth: 0
            }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                Total
              </Typography>
              <Typography variant="h3" sx={{ 
                fontWeight: 700,
                color: '#ea5b0c',
                mb: 1,
                textAlign: 'center'
              }}>
                {metrics.totalTickets}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Tickets au total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', minWidth: 0 }}>
          <Card 
            onClick={() => handleMetricClick('inProgress')}
            sx={{
              background: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                borderColor: '#ff9800'
              }
            }}
          >
            <CardContent sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              width: '100%',
              minWidth: 0
            }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                Actifs
              </Typography>
              <Typography variant="h3" sx={{ 
                fontWeight: 700,
                color: '#ff9800',
                mb: 1,
                textAlign: 'center'
              }}>
                {metrics.inProgressTickets}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                En cours d'intervention
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', minWidth: 0 }}>
          <Card 
            onClick={() => handleMetricClick('completed')}
            sx={{
              background: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                borderColor: '#4caf50'
              }
            }}
          >
            <CardContent sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              width: '100%',
              minWidth: 0
            }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                Résolus
              </Typography>
              <Typography variant="h3" sx={{ 
                fontWeight: 700,
                color: '#4caf50',
                mb: 1,
                textAlign: 'center'
              }}>
                {metrics.completedTickets}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Tickets terminés
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', minWidth: 0 }}>
          <Card 
            onClick={() => handleMetricClick('waiting')}
            sx={{
              background: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                borderColor: '#2196f3'
              }
            }}
          >
            <CardContent sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              width: '100%',
              minWidth: 0
            }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                En attente
              </Typography>
              <Typography variant="h3" sx={{ 
                fontWeight: 700,
                color: '#2196f3',
                mb: 1,
                textAlign: 'center'
              }}>
                {metrics.waitingTickets}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                En attente d'information
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} justifyContent="center">

        <Grid item xs={12} md={6}>
          <Card sx={{
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Avatar sx={{ bgcolor: '#9c27b0', width: 32, height: 32 }}>
                  <Person />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#313635' }}>
                  Répartition des Collaborateurs
                </Typography>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Collaborateur</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actifs</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Terminés</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ticketsByCollaborateur.slice(0, 5).map((item, index) => (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: '#ea5b0c' }}>
                              <Person fontSize="small" />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.collaborateurPrenom} {item.collaborateurNom}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.totalTickets}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={item.activeTickets} 
                            size="small" 
                            color="warning"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={item.completedTickets} 
                            size="small" 
                            color="success"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Avatar sx={{ bgcolor: '#2196f3', width: 32, height: 32 }}>
                  <Business />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#313635' }}>
                  Tickets par Client
                </Typography>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actifs</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Terminés</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ticketsByClient.slice(0, 5).map((item, index) => (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: '#2196f3' }}>
                              <Business fontSize="small" />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.clientNom}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.totalTickets}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={item.activeTickets} 
                            size="small" 
                            color="warning"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={item.completedTickets} 
                            size="small" 
                            color="success"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>


      </Grid>
      </Box>
    </Box>
  );
}

